import React, { useEffect, useRef, useState } from 'react';
import PresenceDot from './PresenceDot';
import EmojiPicker from './EmojiPicker';
import * as STATUS from '../lib/chatStatus';
import './StatusMenu.css';

/**
 * "Available", "Busy", "Do not disturb" - set from under your own name.
 *
 * WHY IT LIVES IN THE ACCOUNT MENU rather than on the chat screen. A status
 * is a fact about you, not about a conversation, and the people who most need
 * to set one - somebody heading into a client meeting, somebody who does not
 * want a laptop ringing - are not on the chat screen when they need it. Teams
 * and Slack both put it under the avatar for the same reason, and the account
 * menu is already where everything that is about you rather than about the
 * page lives.
 *
 * THE LIST COMES FROM THE SERVER. Not a constant in here: the states, their
 * labels and the "clear after" choices are served by /chat/status/options, so
 * there is one statement of what a status may be. A menu that listed them
 * itself would be a second one, and the first time they disagreed somebody
 * would choose an option the server refused.
 *
 * WHAT IT SHOWS WHEN YOU HAVE CHOSEN NOTHING is what everybody else sees -
 * Available, or Away because you have not touched anything, or Busy because
 * you are in a call - with nothing ticked. "Nothing chosen" and "Available"
 * are different, and showing a tick against Available would be a claim
 * nobody made.
 */
export default function StatusMenu({ onDone }) {
  const [open, setOpen] = useState(false);
  const [states, setStates] = useState([]);
  const [clears, setClears] = useState([]);
  const [maxMessage, setMaxMessage] = useState(100);
  const [mine, setMine] = useState(STATUS.myStatus());
  const [message, setMessage] = useState('');
  const [emoji, setEmoji] = useState('');
  const [clearAfter, setClearAfter] = useState('never');
  const [picker, setPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const emojiBtn = useRef(null);

  // The store is the shared truth: the header reads it too, and a second copy
  // in here would be a second thing to keep in step.
  useEffect(() => STATUS.onMyStatus(setMine), []);

  // Asked for once, when the panel is first opened rather than on every
  // render of the navbar: nobody needs the menu's contents until they look.
  useEffect(() => {
    if (!open || states.length > 0) return;
    STATUS.options().then((o) => {
      setStates(o.states || []);
      setClears(o.clear_after || []);
      if (Number.isFinite(o.max_message)) setMaxMessage(o.max_message);
    }).catch(() => setProblem('Statuses are not available on this server yet.'));
  }, [open, states.length]);

  // Opening shows what is actually set, rather than whatever was half-typed
  // last time and abandoned.
  useEffect(() => {
    if (!open) return;
    setMessage(mine?.message || '');
    setEmoji(mine?.emoji || '');
    setClearAfter('never');
    setProblem(null);
  }, [open, mine]);

  const apply = async (next) => {
    setBusy(true);
    setProblem(null);
    try {
      await STATUS.save({
        state: next === undefined ? (mine?.chosen || null) : next,
        message,
        emoji,
        clearAfter
      });
      setOpen(false);
      if (onDone) onDone();
    } catch (e) {
      setProblem(e?.response?.data?.error || 'That could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  const wipe = async () => {
    setBusy(true);
    try {
      await STATUS.clear();
      setMessage('');
      setEmoji('');
      setOpen(false);
      if (onDone) onDone();
    } catch (e) {
      setProblem(e?.response?.data?.error || 'That could not be cleared.');
    } finally {
      setBusy(false);
    }
  };

  const shown = mine?.state || 'offline';
  const shownLabel = mine?.label || 'Offline';

  return (
    <div className="us">
      <button
        type="button"
        className="am-item us-trigger"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="us-dot-wrap" aria-hidden="true">
          <PresenceDot state={shown} label={shownLabel} className="pd-inline" />
        </span>
        <span className="am-label us-label">
          {shownLabel}
          {/* What they chose is not always what is shown - in a call, or away
              from the keyboard - so the reason rides alongside rather than
              replacing it. */}
          {mine?.detail && <span className="us-detail">{mine.detail}</span>}
          {mine?.message && (
            <span className="us-msg">
              {mine.emoji ? `${mine.emoji} ` : ''}{mine.message}
            </span>
          )}
        </span>
        <svg className={`am-chev am-chev-item${open ? ' is-open' : ''}`}
             viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.9"
             strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 6.5 8 10.5 12 6.5" />
        </svg>
      </button>

      {open && (
        <div className="us-panel">
          {problem && <p className="us-problem">{problem}</p>}

          <ul className="us-states">
            {states.map((s) => (
              <li key={s.key}>
                <button
                  type="button"
                  className="us-state"
                  aria-pressed={mine?.chosen === s.key}
                  disabled={busy}
                  onClick={() => apply(s.key)}
                >
                  <PresenceDot state={s.dot} label={s.label} className="pd-inline" />
                  <span className="us-state-text">
                    <span className="us-state-name">{s.label}</span>
                    {/* Said before it is pressed, not after it is missed:
                        Do not disturb stops calls ringing. */}
                    {s.note && <span className="us-state-note">{s.note}</span>}
                  </span>
                  {mine?.chosen === s.key && <span className="us-tick" aria-hidden="true">✓</span>}
                </button>
              </li>
            ))}
          </ul>

          <div className="us-sep" />

          <label className="us-field">
            <span className="us-field-label">What you are doing</span>
            <span className="us-msg-row">
              <button
                type="button"
                className="us-emoji"
                ref={emojiBtn}
                onClick={() => setPicker(true)}
                aria-label="Choose an emoji"
              >
                {emoji || '🙂'}
              </button>
              <input
                type="text"
                className="us-input"
                value={message}
                maxLength={maxMessage}
                placeholder="On site at client until 3"
                onChange={(e) => setMessage(e.target.value)}
              />
            </span>
          </label>

          <label className="us-field">
            <span className="us-field-label">Clear it</span>
            <select
              className="us-select"
              value={clearAfter}
              onChange={(e) => setClearAfter(e.target.value)}
            >
              {clears.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </label>

          <div className="us-actions">
            <button type="button" className="us-save" disabled={busy}
                    onClick={() => apply(undefined)}>
              Save
            </button>
            <button type="button" className="us-clear" disabled={busy} onClick={wipe}>
              Clear status
            </button>
          </div>
        </div>
      )}

      {picker && (
        <EmojiPicker
          anchor={emojiBtn.current}
          mode="compose"
          label="Choose an emoji"
          onPick={(e) => { setEmoji(typeof e === 'string' ? e : e?.char || ''); setPicker(false); }}
          onClose={() => setPicker(false)}
        />
      )}
    </div>
  );
}
